import React, { useEffect, useState, createContext, useContext } from 'react';
import { supabase } from '../lib/supabase';
interface AuthUser {
  id: string;
  email: string;
}
interface AuthContextType {
  adminUser: AuthUser | null;
  isAdminLoading: boolean;
  adminLogin: (email: string, password: string) => Promise<{
    error: string | null;
  }>;
  adminLogout: () => Promise<void>;
}
const AuthContext = createContext<AuthContextType | undefined>(undefined);
export function AuthProvider({
  children
}: {
  children: ReactNode;
}) {
  const [adminUser, setAdminUser] = useState<AuthUser | null>(null);
  const [isAdminLoading, setIsAdminLoading] = useState(true);
  useEffect(() => {
    // Check for existing session on mount
    supabase.auth.getSession().then(({
      data: {
        session
      }
    }) => {
      if (session?.user) {
        setAdminUser({
          id: session.user.id,
          email: session.user.email || ''
        });
      }
      setIsAdminLoading(false);
    });
    // Listen for auth state changes
    const {
      data: {
        subscription
      }
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        setAdminUser({
          id: session.user.id,
          email: session.user.email || ''
        });
      } else {
        setAdminUser(null);
      }
    });
    return () => subscription.unsubscribe();
  }, []);
  const adminLogin = async (email: string, password: string): Promise<{
    error: string | null;
  }> => {
    const {
      data,
      error
    } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    if (error) {
      return {
        error: error.message
      };
    }
    if (data.user) {
      setAdminUser({
        id: data.user.id,
        email: data.user.email || ''
      });
    }
    return {
      error: null
    };
  };
  const adminLogout = async () => {
    await supabase.auth.signOut();
    setAdminUser(null);
  };
  return <AuthContext.Provider value={{
    adminUser,
    isAdminLoading,
    adminLogin,
    adminLogout
  }} data-id="element-1410">
      {children}
    </AuthContext.Provider>;
}
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}